import React from 'react';
import { Button, Dropdown } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import { SearchOptionType } from '../helpers/types';
import { SEARCH_OPTION_DEFINITIONS } from '../SearchOptions/constants';
import { useDropdownNavigableTrigger } from './useDropdownNavigableTrigger';
import { useSearchMode, useSpotlightDropdownContext, useSearchScope } from '../SpotlightState';

export function SearchOptionsDropdown() {
	const { selectedSearchOptions, setSelectedSearchOptions } = useSearchScope();
	const { isDropdownOpen } = useSpotlightDropdownContext();
	const { isSearchOptionsDisabled } = useSearchMode();
	const { translate } = useTranslate();
	const { isActive, onOpen, onClose, getStableTriggerRef } =
		useDropdownNavigableTrigger<HTMLButtonElement>(isDropdownOpen, isSearchOptionsDisabled);

	const toggleSearchOption = (key: SearchOptionType) => {
		setSelectedSearchOptions((prev) => {
			if (prev.some((option) => option.key === key)) {
				return prev.filter((option) => option.key !== key);
			}

			return [...prev, SEARCH_OPTION_DEFINITIONS[key].parseFromQuery('')];
		});
	};

	const isChecked = (key: SearchOptionType) =>
		selectedSearchOptions.some((option) => option.key === key);

	const isTitleChecked = isChecked(SearchOptionType.Title);
	const isDescriptionChecked = isChecked(SearchOptionType.Description);

	const getTriggerLabel = () => {
		if (isTitleChecked && isDescriptionChecked) {
			return translate('spotlight.searchOptionsDropdown.trigger.label.all');
		}

		if (isTitleChecked) {
			return translate('spotlight.searchOptions.chips.title');
		}

		if (isDescriptionChecked) {
			return translate('spotlight.searchOptions.chips.description');
		}

		return translate('spotlight.searchOptions.search-in');
	};

	return (
		<Dropdown
			onOpen={onOpen}
			onClose={onClose}
			trigger={({ isOpen, ref, ...triggerProps }, { open, close }) => (
				<Button
					ref={getStableTriggerRef({ open, close, ref })}
					isPressed={isOpen || (!isSearchOptionsDisabled && isActive)}
					isDisabled={isSearchOptionsDisabled}
					rightIcon={<Dropdown.Arrow />}
					variant="clean"
					isSmall
					{...triggerProps}
				>
					<Dropdown.SelectButtonText>{getTriggerLabel()}</Dropdown.SelectButtonText>
				</Button>
			)}
			position="bottom-right"
		>
			<Dropdown.SectionTitle>
				{translate('spotlight.searchOptionsDropdown.title')}
			</Dropdown.SectionTitle>

			<Dropdown.Item
				isChecked={isTitleChecked}
				checkedVariant="checkbox"
				onClick={() => toggleSearchOption(SearchOptionType.Title)}
			>
				{translate('spotlight.searchOptions.chips.title')}
			</Dropdown.Item>

			<Dropdown.Item
				isChecked={isDescriptionChecked}
				checkedVariant="checkbox"
				onClick={() => toggleSearchOption(SearchOptionType.Description)}
			>
				{translate('spotlight.searchOptions.chips.description')}
			</Dropdown.Item>
		</Dropdown>
	);
}
